import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";

export default function Coach() {
  const navigate = useNavigate();
  const [params, setSearchParams] = useSearchParams();
  const { user, isAuthed, loginGoogle } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [meta, setMeta] = useState(null); // { coach_cap, remaining, has_plan, plan_name }
  const [loaded, setLoaded] = useState(false);
  const [fromStuck, setFromStuck] = useState(false);
  const stuckSent = useRef(false);
  const endRef = useRef(null);

  useEffect(() => {
    if (!isAuthed) return;
    api.get("/coach").then(({ data }) => {
      setMessages(data.messages || []);
      setMeta(data);
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, [isAuthed]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, sending]);

  const send = useCallback(async (textArg) => {
    const text = (typeof textArg === "string" ? textArg : input).trim();
    if (!text || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text, _local: true }]);
    setSending(true);
    try {
      const { data } = await api.post("/coach", { message: text });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
      setMeta((mt) => ({ ...(mt || {}), coach_used: data.coach_used, remaining: data.remaining, coach_cap: data.coach_cap }));
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 403) toast.error(detail || "Tu as atteint ta limite de messages ce mois-ci.");
      else if (status === 402) toast.error(detail || "Crédits insuffisants ce mois-ci.");
      else toast.error(detail || "Le coach n'a pas pu répondre, réessaie.");
      setMessages((m) => m.filter((x) => !x._local));
      setInput(text);
    } finally {
      setSending(false);
    }
  }, [input, sending]);

  // "Je suis bloqué" deep-link: pre-fill and auto-send the context once the coach is ready.
  useEffect(() => {
    const stuck = params.get("stuck");
    if (!stuck || !loaded || !isAuthed || stuckSent.current) return;
    if (!meta?.has_plan) return;
    stuckSent.current = true;
    setFromStuck(true);
    send(stuck);
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, loaded, isAuthed, meta]);

  if (user === null) return <div className="center-page" role="status"><p className="eyebrow">Chargement...</p></div>;

  if (!isAuthed) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <main className="container page-content fade-in lookup-layout">
          <div><p className="eyebrow">COACH IA</p><h1>Connecte-toi.</h1><p className="muted mt-6">Ton coach business personnel t'attend.</p></div>
          <div className="lookup-form">
            <button onClick={() => navigate("/retrouver")} data-testid="coach-email-login" className="btn-primary w-full">Me connecter par email</button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const hasPlan = meta?.has_plan;
  const remaining = meta?.remaining ?? 0;
  const cap = meta?.coach_cap ?? 0;
  const unlimited = cap >= 300; // Accélération: fair-use, shown as "illimité"

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="container page-content fade-in coach-page" data-testid="coach-page">
        <div className="coach-head">
          <div>
            <p className="eyebrow">COACH IA</p>
            <h1>Ton coach <em>business.</em></h1>
          </div>
          {hasPlan && (
            <span className="coach-quota" data-testid="coach-quota">
              {unlimited ? "Messages illimités" : `${remaining} / ${cap} messages ce mois-ci`}
            </span>
          )}
        </div>

        {fromStuck && (
          <div className="coach-stuck-note" data-testid="coach-stuck-note">
            <p className="text-sm">Ton coach te donne une première action simple ci-dessous. 👇</p>
            <p className="text-sm mt-1 muted">Toujours bloqué ? Écris au support : <a href="mailto:support.idevorai@gmail.com" className="accent">support.idevorai@gmail.com</a></p>
          </div>
        )}

        {!hasPlan && loaded ? (
          <div className="coach-locked" data-testid="coach-locked">
            <p className="muted">Le coach IA fait partie des formules. Choisis une offre pour discuter avec ton coach business tous les jours.</p>
            <button onClick={() => navigate("/tarifs")} data-testid="coach-see-plans" className="btn-primary mt-6">Voir les formules <span aria-hidden="true">→</span></button>
          </div>
        ) : (
          <>
            <div className="coach-thread" data-testid="coach-thread">
              {messages.length === 0 && loaded && (
                <div className="coach-empty" data-testid="coach-empty">
                  <p>Salut {user.name || ""} 👋 Pose-moi ta première question : par quoi tu veux commencer aujourd'hui ?</p>
                </div>
              )}
              {messages.map((m, i) => (
                <div key={i} className={`coach-bubble ${m.role === "user" ? "user" : "coach"}`} data-testid={`coach-msg-${m.role}-${i}`}>
                  {m.content}
                </div>
              ))}
              {sending && <div className="coach-bubble coach coach-typing" data-testid="coach-typing"><span></span><span></span><span></span></div>}
              <div ref={endRef} />
            </div>

            <form className="coach-input-row" onSubmit={(e) => { e.preventDefault(); send(); }} data-testid="coach-form">
              <input
                type="text"
                className="field coach-input"
                data-testid="coach-input"
                placeholder="Écris à ton coach…"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                disabled={sending}
              />
              <button type="submit" className="btn-primary coach-send" data-testid="coach-send" disabled={sending || !input.trim()} aria-label="Envoyer">
                <Send size={18} />
              </button>
            </form>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
